<template>
  <div class="rounded-xl bg-[#f9f9fa] px-3 py-2.5">
    <div class="text-[12px] leading-4 text-gray-500">{{ label }}</div>
    <div class="mt-1.5">
      <div
        v-if="results.source === 'audio'"
        class="flex items-center gap-x-2.5"
      >
        <button
          type="button"
          :aria-label="playing ? 'Pause recording' : 'Play recording'"
          class="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-100"
          @click="toggle"
        >
          <component
            :is="playing ? PauseIcon : PlayIcon"
            class="size-4 text-gray-900"
            aria-hidden="true"
          />
        </button>
        <div
          class="flex h-9 min-w-0 flex-1 items-center gap-[1.5px] overflow-hidden"
        >
          <span
            v-for="(peak, index) in results.peaks"
            :key="index"
            class="w-[2px] shrink-0 rounded-[1px]"
            :class="
              index / results.peaks.length < progress
                ? 'bg-gray-700'
                : 'bg-gray-300'
            "
            :style="{ height: `${Math.max(2, peak * 36)}px` }"
          />
        </div>
        <audio
          ref="audio"
          :src="audioUrl"
          @timeupdate="handleTime"
          @play="playing = true"
          @pause="playing = false"
          @ended="handleEnded"
        />
      </div>

      <div
        v-else-if="results.source === 'visual'"
        class="flex h-11 overflow-hidden rounded-[4px] bg-gray-200"
      >
        <img
          v-for="frame in filmstrip"
          :key="frame"
          :src="frame"
          alt=""
          class="h-11 min-w-0 flex-1 object-cover"
        />
      </div>

      <div v-else-if="results.media" class="flex items-center gap-x-2.5">
        <div class="size-[30px] shrink-0">
          <Thumbnail
            :type="fileTypeToThumbnail(mediaFileType(results.media))"
          />
        </div>
        <span class="truncate text-[14px] leading-[18px] text-gray-800">
          {{ results.media.name }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { Thumbnail } from '@dacr/library'
import { PauseIcon, PlayIcon } from '@dacr/icons'

import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'

import { mediaFileType } from './report'
import type { CheckerResults } from './results'

const props = defineProps<{ results: CheckerResults }>()

const audio = ref<HTMLAudioElement>()
const playing = ref(false)
const progress = ref(0)

const label = computed(() =>
  props.results.source === 'audio'
    ? 'Recorded audio'
    : props.results.source === 'visual'
      ? 'Captured visual'
      : 'Uploaded file'
)

const audioUrl =
  props.results.source === 'audio' && props.results.media
    ? URL.createObjectURL(props.results.media)
    : undefined

const filmstrip = computed(() => {
  const frames = props.results.frames
  if (frames.length <= 5) return frames
  return Array.from(
    { length: 5 },
    (_, index) => frames[Math.round((index * (frames.length - 1)) / 4)]
  )
})

const toggle = () => {
  const element = audio.value
  if (!element) return
  if (element.paused) void element.play()
  else element.pause()
}

const handleTime = () => {
  const element = audio.value
  if (element?.duration) progress.value = element.currentTime / element.duration
}

const handleEnded = () => {
  playing.value = false
  progress.value = 0
}

onBeforeUnmount(() => {
  if (audioUrl) URL.revokeObjectURL(audioUrl)
})
</script>
